import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { setBusinessInfo } from "../../../store/business/businessReducer";

const BusinessInfo = ({ onNext }: { onNext: () => void }) => {
    const dispatch = useDispatch();

    // Validation schema
    const validationSchema = Yup.object({
        businessName: Yup.string().required("Business name is required"),
        phone: Yup.string()
            .matches(/^\d{10}$/, "Phone number must be 10 digits")
            .required("Phone number is required"),
        ownerName: Yup.string().required("Owner name is required"),
        businessEmail: Yup.string()
            .email("Invalid email address")
            .required("Business email is required"),
        address: Yup.object({
            line1: Yup.string().required("Address line 1 is required"),
            city: Yup.string().required("City is required"),
            state: Yup.string().required("State is required"),
            country: Yup.string().required("Country is required"),
            zipCode: Yup.string()
                .matches(/^\d{5}$/, "Zip code must be 5 digits")
                .required("Zip code is required"),
        }),
    });

    // Formik configuration
    const formik = useFormik({
        initialValues: {
            businessName: "",
            phone: "",
            ownerName: "",
            businessEmail: "",
            address: {
                line1: "",
                line2: "",
                city: "",
                state: "",
                country: "",
                zipCode: "",
            },
        },
        validationSchema,
        onSubmit: (values) => {
            dispatch(setBusinessInfo(values)); // Save to Redux state
            onNext(); // Move to the next step
        },
    });

    return (
        <form onSubmit={formik.handleSubmit} className="space-y-6">
            <h2 className="text-lg font-bold mb-6">Business Information</h2>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Business Name */}
                <div>
                    <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                        Business Name
                    </label>
                    <input
                        id="businessName"
                        type="text"
                        {...formik.getFieldProps("businessName")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.businessName && formik.errors.businessName
                            ? "border-red-500"
                            : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                    {formik.touched.businessName && formik.errors.businessName && (
                        <div className="text-red-500 text-sm">{formik.errors.businessName}</div>
                    )}
                </div>

                {/* Phone Number */}
                <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                        Phone Number
                    </label>
                    <input
                        id="phone"
                        type="text"
                        {...formik.getFieldProps("phone")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.phone && formik.errors.phone ? "border-red-500" : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                    {formik.touched.phone && formik.errors.phone && (
                        <div className="text-red-500 text-sm">{formik.errors.phone}</div>
                    )}
                </div>

                {/* Owner's Name */}
                <div>
                    <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700">
                        Owner's Name
                    </label>
                    <input
                        id="ownerName"
                        type="text"
                        {...formik.getFieldProps("ownerName")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.ownerName && formik.errors.ownerName
                            ? "border-red-500"
                            : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                    {formik.touched.ownerName && formik.errors.ownerName && (
                        <div className="text-red-500 text-sm">{formik.errors.ownerName}</div>
                    )}
                </div>

                {/* Business Email */}
                <div>
                    <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700">
                        Business Email
                    </label>
                    <input
                        id="businessEmail"
                        type="email"
                        {...formik.getFieldProps("businessEmail")}
                        className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.businessEmail && formik.errors.businessEmail
                            ? "border-red-500"
                            : "border-gray-300"
                            } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                    />
                    {formik.touched.businessEmail && formik.errors.businessEmail && (
                        <div className="text-red-500 text-sm">{formik.errors.businessEmail}</div>
                    )}
                </div>

                {/* Address Fields */}
                {["line1", "line2", "city", "state", "country", "zipCode"].map((field) => (
                    <div key={field}>
                        <label
                            htmlFor={`address.${field}`}
                            className="block text-sm font-medium text-gray-700 capitalize"
                        >
                            {field === "line2" ? "Address Line 2 (Optional)" : `Address ${field}`}
                        </label>
                        <input
                            id={`address.${field}`}
                            type="text"
                            {...formik.getFieldProps(`address.${field}`)}
                            className={`px-2 mt-1 py-3 block w-full rounded-md border ${formik.touched.address?.[field] && formik.errors.address?.[field]
                                ? "border-red-500"
                                : "border-gray-300"
                                } shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm`}
                        />
                        {formik.touched.address?.[field] && formik.errors.address?.[field] && (
                            <div className="text-red-500 text-sm">{formik.errors.address?.[field]}</div>
                        )}
                    </div>
                ))}
            </div>
            <div className="mt-6 flex justify-end">
                <button
                    type="submit"
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default BusinessInfo;
